"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RolePermissionsSchema = exports.AccessLevelSchema = exports.ADMIN_SECTION_GROUPS = exports.ADMIN_SECTIONS = exports.ALWAYS = exports.OWNER_ROLE_ID = void 0;
exports.requiredLevel = requiredLevel;
exports.levelFor = levelFor;
exports.allows = allows;
exports.allEdit = allEdit;
exports.allView = allView;
exports.legacyToLevels = legacyToLevels;
exports.canGrant = canGrant;
exports.matchRoute = matchRoute;
/**
 * SUPER-ADMIN ROLES & ACCESS — the single source of truth shared by
 * super-admin-ui (menu + route guard + roles screen), staff-service (grant
 * rules) and admin-access (the API authorizer).
 *
 * A section IS a super-admin-ui route path, so roles saved before levels
 * existed (string[] of paths, '*') convert without loss: legacyToLevels().
 * Spec: docs/superpowers/specs/2026-10-03-super-admin-rbac-design.md
 */
const zod_1 = require("zod");
exports.OWNER_ROLE_ID = 'role_owner';
/** Route-map section value for self-service routes every signed-in admin may call. */
exports.ALWAYS = 'always';
exports.ADMIN_SECTIONS = [
    { id: '/command-center', label: 'Command Center', group: 'Overview' },
    { id: '/command-center/leads', label: 'Leads', group: 'Overview' },
    { id: '/command-center/review', label: 'Lead review', group: 'Overview' },
    { id: '/finance', label: 'Finance Hub', group: 'Overview', sensitive: true },
    { id: '/tenants-list', label: 'Tenants', group: 'Overview' },
    { id: '/payments', label: 'Payments', group: 'Overview', sensitive: true },
    { id: '/ai', label: 'AI Control Center', group: 'AI & Analytics' },
    { id: '/analytics', label: 'Website Analytics', group: 'AI & Analytics' },
    { id: '/affiliates', label: 'Affiliates', group: 'Affiliates' },
    { id: '/affiliate-dashboard', label: 'Affiliate Dashboard', group: 'Affiliates' },
    { id: '/commission-plans', label: 'Commission Plans', group: 'Affiliates', sensitive: true },
    { id: '/menus', label: 'Menus', group: 'Configuration' },
    { id: '/packages', label: 'Packages', group: 'Configuration' },
    { id: '/products', label: 'Products', group: 'Configuration' },
    { id: '/integrations', label: 'Integrations', group: 'Configuration', sensitive: true },
    { id: '/deals', label: 'Deals', group: 'Configuration' },
    { id: '/tools', label: 'Tools', group: 'Tools' },
    { id: '/tools/activity', label: 'Tool Activity', group: 'Tools' },
    { id: '/tools/data-migration', label: 'Data Migration', group: 'Tools' },
    { id: '/deployments', label: 'Deployments', group: 'Deployment & Release', sensitive: true },
    { id: '/releases', label: 'Releases', group: 'Deployment & Release', sensitive: true },
    { id: '/mobile-apps', label: 'Mobile Apps', group: 'Deployment & Release' },
    { id: '/pos-config', label: 'Device Configurations', group: 'Deployment & Release' },
    { id: '/pos-defaults', label: 'POS Defaults', group: 'Deployment & Release' },
    { id: '/fleet-monitoring', label: 'Fleet Monitoring', group: 'Deployment & Release' },
    { id: '/inventory/stock', label: 'Devices', group: 'Inventory' },
    { id: '/inventory/finance-book', label: 'Finance Book', group: 'Inventory', sensitive: true },
    { id: '/inventory/receiving', label: 'Receiving', group: 'Inventory' },
    { id: '/inventory/vendors', label: 'Vendors', group: 'Inventory' },
    { id: '/inventory/adjustments', label: 'Adjustments', group: 'Inventory' },
    { id: '/inventory/damage', label: 'Damage', group: 'Inventory' },
    { id: '/inventory/provisioning', label: 'Provisioning', group: 'Inventory' },
    { id: '/inventory/brands', label: 'Brands', group: 'Inventory' },
    { id: '/inventory/sales', label: 'Sales', group: 'Inventory' },
    { id: '/inventory/orders', label: 'Orders', group: 'Inventory' },
    { id: '/business-transfers', label: 'Business transfers', group: 'Inventory' },
    { id: '/inventory/financing', label: 'Financing', group: 'Inventory', sensitive: true },
    { id: '/inventory/agreements', label: 'Agreements', group: 'Inventory' },
    { id: '/inventory/reports', label: 'Reports', group: 'Inventory' },
    { id: '/legal/policies', label: 'Policies', group: 'Legal' },
    { id: '/legal/agreements', label: 'Agreements', group: 'Legal' },
    { id: '/legal/categories', label: 'Categories', group: 'Legal' },
    { id: '/legal/company', label: 'Company Profile', group: 'Legal' },
    { id: '/legal/packs', label: 'Packs', group: 'Legal' },
    { id: '/legal/bindings', label: 'Bindings', group: 'Legal' },
    { id: '/legal/hub', label: 'Documents Hub', group: 'Legal' },
    { id: '/legal/signatures', label: 'Signatures', group: 'Legal' },
    { id: '/notifications', label: 'Notifications', group: 'System' },
    { id: '/support', label: 'Support Tickets', group: 'System' },
    { id: '/app-issues', label: 'Issue Console', group: 'System' },
    { id: '/controls', label: 'Controls', group: 'System', sensitive: true },
    { id: '/activity', label: 'Activity Log', group: 'System' },
    { id: '/staff', label: 'Team Management', group: 'Team', sensitive: true },
    { id: '/staff/roles', label: 'Roles & Access', group: 'Team', sensitive: true },
    { id: '/estate-admin', label: 'Ruby Estate', group: 'Ruby Estate', sensitive: true },
    // Task 19 — identity-service admin settings (Stripe restricted key, webhook, DLQ/open-checks
    // status). Sensitive: this gates where the Stripe key is set, even though the key value
    // itself is never returned by the API.
    { id: '/identity-admin', label: 'ID Verification', group: 'System', sensitive: true },
];
exports.ADMIN_SECTION_GROUPS = Array.from(new Set(exports.ADMIN_SECTIONS.map(s => s.group)));
const SECTION_IDS = new Set(exports.ADMIN_SECTIONS.map(s => s.id));
exports.AccessLevelSchema = zod_1.z.enum(['view', 'edit']);
exports.RolePermissionsSchema = zod_1.z.record(zod_1.z.string(), exports.AccessLevelSchema)
    .refine(p => Object.keys(p).every(k => SECTION_IDS.has(k)), { message: 'unknown section' });
const RANK = { view: 1, edit: 2 };
function requiredLevel(method) {
    const m = String(method || '').toUpperCase();
    return m === 'GET' || m === 'HEAD' || m === 'OPTIONS' ? 'view' : 'edit';
}
function levelFor(perms, sectionId) {
    const l = perms?.[sectionId];
    return l === 'view' || l === 'edit' ? l : null;
}
function allows(perms, sectionId, needed) {
    if (!SECTION_IDS.has(sectionId))
        return false;
    const have = levelFor(perms, sectionId);
    return !!have && RANK[have] >= RANK[needed];
}
function allEdit() {
    return Object.fromEntries(exports.ADMIN_SECTIONS.map(s => [s.id, 'edit']));
}
function allView() {
    return Object.fromEntries(exports.ADMIN_SECTIONS.map(s => [s.id, 'view']));
}
/** string[] (legacy paths, '*', parent paths) or a level map → a clean level map. Garbage → {}. */
function legacyToLevels(input) {
    if (Array.isArray(input)) {
        const paths = input.map(String);
        if (paths.includes('*'))
            return allEdit();
        const out = {};
        for (const s of exports.ADMIN_SECTIONS) {
            if (paths.some(p => s.id === p || s.id.startsWith(`${p}/`)))
                out[s.id] = 'edit';
        }
        return out;
    }
    if (input && typeof input === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(input)) {
            if (SECTION_IDS.has(k) && (v === 'view' || v === 'edit'))
                out[k] = v;
        }
        return out;
    }
    return {};
}
function canGrant(callerPerms, target, callerIsOwner) {
    if (callerIsOwner)
        return { ok: true };
    for (const [sectionId, level] of Object.entries(target)) {
        if (!allows(callerPerms, sectionId, level))
            return { ok: false, sectionId };
    }
    return { ok: true };
}
const norm = (r) => (r.length > 1 && r.endsWith('/') ? r.slice(0, -1) : r);
/** Exact template match (API Gateway `event.resource`), method-specific entries win over '*'. */
function matchRoute(map, api, method, resource) {
    const res = norm(resource);
    const m = String(method).toUpperCase();
    let wildcard = null;
    for (const r of map) {
        if (r.api !== api || norm(r.resource) !== res)
            continue;
        if (r.method.toUpperCase() === m)
            return r;
        if (r.method === '*')
            wildcard = r;
    }
    return wildcard;
}
//# sourceMappingURL=adminAccess.js.map